<template>
  <FinderCard data-test="finder-price-card" :title="criterion.title">
    <div>
      <div class="grid grid-cols-3 gap-8">
        <RevChip
          v-for="preset in criterion.presets"
          :key="preset.id"
          class="body-1 w-full"
          :data-test="`budget-preset-${preset.id}`"
          :label="
            i18n.price(preset.maxValue, {
              currency: defaultCurrency,
              maximumFractionDigits: 0,
            })
          "
          :model-value="isPresetActive(preset.id)"
          size="medium"
          @click="togglePreset(preset.id)"
        />
      </div>

      <div v-if="priceFacet" class="mt-12">
        <div class="relative h-[92px]">
          <Graph
            class="absolute inset-x-0 top-0 h-[72px]"
            data-test="budget-graph"
            :items="priceFacet.values"
            :max-selected="priceRange[1]"
            :min-selected="priceRange[0]"
            :step="priceFacet.step"
          />
          <RevRange
            v-model="priceRange"
            class="absolute inset-x-0 bottom-0 h-[28px]"
            data-test="budget-range"
            :disabled="!isReady"
            :label="[minLabel, maxLabel]"
            :max="priceFacet.max"
            :step="priceFacet.step"
          />
        </div>
        <div class="mt-16 flex justify-between gap-8">
          <PriceInput
            id="budget-min-price"
            v-model="priceRange[0]"
            class="w-96"
            :label="minLabel"
            :max="priceFacet.max"
            :min="0"
          />
          <PriceInput
            id="budget-max-price"
            v-model="priceRange[1]"
            class="w-96"
            :label="maxLabel"
            :max="priceFacet.max"
            :min="0"
          />
        </div>
      </div>
    </div>
  </FinderCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'

import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import Graph from '@backmarket/nuxt-layer-recommendation/filtering/Graph.vue'
import PriceInput from '@backmarket/nuxt-layer-recommendation/filtering/PriceInput.vue'
import type { UiPriceFacet } from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevChip } from '@ds/components/Chip'
import { RevRange } from '@ds/components/Range'
import { watchDebounced } from '@vueuse/core'

import { isBudgetPresetSelected } from '../../../../composables/useFinder/isBudgetPresetSelected'
import type { FinderBudgetCriterion } from '../../../../composables/useFinder/useFinder.types'
import { useFinderTracking } from '../../../../composables/useFinder/useFinderTracking'
import FinderCard from '../FinderCard/FinderCard.vue'

import translations from './FinderPriceTile.translations'

const props = defineProps<{
  criterion: FinderBudgetCriterion
  priceFacet?: UiPriceFacet
}>()

const model = defineModel<[number, number] | undefined>()

const i18n = useI18n()
const { defaultCurrency } = useMarketplace().market
const { trackFinderInteraction } = useFinderTracking()

const max = computed(() => props.priceFacet?.max ?? 0)
const priceRange = ref<[number, number]>(model.value ?? [0, max.value])
const isReady = ref(false)

// WHY: copies so `model` (the committed state) and `priceRange` (the UI
// state) never share an array reference — `priceRange` is mutated in place
// by the inputs, which must never be visible through `model`.
watch(
  model,
  (newModelValue) => {
    priceRange.value = newModelValue ? [...newModelValue] : [0, max.value]
  },
  { deep: true },
)

watch(max, (newMax) => {
  if (!model.value) {
    priceRange.value = [0, newMax]
  }
})

watchDebounced(
  priceRange,
  (newRange) => {
    const isFullRange = newRange[0] === 0 && newRange[1] === max.value
    const previousRange = model.value ?? [0, max.value]

    model.value = isFullRange ? undefined : [...newRange]

    if (isFullRange) return

    // WHY: diffing against the committed `model` (instead of the watcher's
    // old value) ignores updates synced from outside the tile (preset
    // clicks, classic filter UI bound to the same model), so only genuine
    // in-tile user input is tracked.
    if (newRange[0] !== previousRange[0]) {
      trackFinderInteraction({ value: 'minSlider' })
    }
    if (newRange[1] !== previousRange[1]) {
      trackFinderInteraction({ value: 'maxSlider' })
    }
  },
  { deep: true, debounce: 500 },
)

onMounted(() => {
  isReady.value = true
})

const minLabel = computed(() =>
  i18n(translations.minPrice, { currency: defaultCurrency as Currency }),
)
const maxLabel = computed(() =>
  i18n(translations.maxPrice, { currency: defaultCurrency as Currency }),
)

function isPresetActive(presetId: string): boolean {
  const preset = props.criterion.presets.find((p) => p.id === presetId)
  if (!preset) return false

  return isBudgetPresetSelected(model.value, preset)
}

function togglePreset(presetId: string): void {
  const presetIndex = props.criterion.presets.findIndex(
    (p) => p.id === presetId,
  )
  const preset = props.criterion.presets[presetIndex]
  if (!preset) return

  trackFinderInteraction({
    value: i18n.price(preset.maxValue, {
      currency: defaultCurrency,
      maximumFractionDigits: 0,
    }),
    position: presetIndex + 1,
  })

  if (isPresetActive(presetId)) {
    model.value = undefined
  } else {
    model.value = [0, preset.maxValue]
  }
}
</script>
